import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  shouldSuppressSessionModal,
  SESSION_EXPIRED_UI_KEY,
  clearSessionExpiredPrompt,
} from "../../auth/sessionUnauthorizedEvents";

function readPendingPrompt(): boolean {
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_UI_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Listens for 401/403 from axios/fetch and Cognito refresh failures.
 * User must sign in again (logout clears stale client state).
 */
const SessionExpiredGate: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { showToast } = useToast();

  /** Close overlay after successful login (flag cleared in AuthContext) */
  useEffect(() => {
    if (isAuthenticated && !readPendingPrompt()) {
      setOpen(false);
    }
  }, [isAuthenticated]);

  const handleSignInAgain = useCallback(() => {
    clearSessionExpiredPrompt();
    setOpen(false);
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  /** Restore dialog after redirect to /login (SPA) or full reload if flag persisted */
  useEffect(() => {
    if (readPendingPrompt()) {
      setOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const onUnauthorized = () => {
      const pending = readPendingPrompt();
      if (shouldSuppressSessionModal(location.pathname) && !pending) {
        return;
      }
      setOpen(true);
    };

    window.addEventListener(
      "app:session-unauthorized",
      onUnauthorized as EventListener
    );
    return () =>
      window.removeEventListener(
        "app:session-unauthorized",
        onUnauthorized as EventListener
      );
  }, [location.pathname]);

  useEffect(() => {
    const onCreditInsufficient = () => {
      showToast("Insufficient credits — add more to continue.", "error", {
        label: "Add Credits",
        onClick: () => navigate("/billing"),
      });
    };
    window.addEventListener("app:credit-insufficient", onCreditInsufficient);
    return () => window.removeEventListener("app:credit-insufficient", onCreditInsufficient);
  }, [showToast, navigate]);

  useEffect(() => {
    const onForbidden = () => {
      showToast("You don't have access to this Brand or action. Switch Brand, or ask its Owner for access.", "error");
    };
    window.addEventListener("app:access-forbidden", onForbidden);
    return () => window.removeEventListener("app:access-forbidden", onForbidden);
  }, [showToast]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-desc"
    >
      <div className="w-full max-w-md rounded-xl border border-zinc-200 bg-white p-6 shadow-xl">
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-800">
          <span className="material-icons-outlined text-2xl">lock_clock</span>
        </div>
        <h2
          id="session-expired-title"
          className="text-lg font-semibold text-zinc-900"
        >
          Sign in required
        </h2>
        <p
          id="session-expired-desc"
          className="mt-2 text-sm leading-relaxed text-zinc-600"
        >
          Your session has expired. Please sign in again to continue.
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleSignInAgain}
            className="rounded-lg bg-brand-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          >
            Sign in again
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionExpiredGate;
